import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../components/ui/table";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { 
  Search, RefreshCw, ChevronLeft, ChevronRight, Loader2, 
  RotateCcw, SkipForward, Calendar
} from "lucide-react";
import { toast } from "sonner";

const QuarterExplorerPage = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("");
  const [cikFilter, setCikFilter] = useState("");
  const [statusCounts, setStatusCounts] = useState({});
  const limit = 50;

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/funds/quarters/all?limit=${limit}&offset=${offset}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (yearFilter) url += `&year=${yearFilter}`;
      if (quarterFilter) url += `&quarter=${quarterFilter}`;
      if (cikFilter) url += `&cik=${cikFilter}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
        setTotal(data.total || 0);
        setStatusCounts(data.status_counts || {});
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
    } finally {
      setLoading(false);
    }
  }, [token, offset, statusFilter, yearFilter, quarterFilter, cikFilter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const retryTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/funds/quarters/${taskId}/retry`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Task reset to pending");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to retry task");
    }
  };

  const skipTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/funds/quarters/${taskId}/skip`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Task marked as skipped");
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to skip task");
    }
  };

  const refreshQuarter = async () => {
    if (!yearFilter || !quarterFilter) {
      toast.error("Select year and quarter first");
      return;
    }
    try {
      const response = await fetch(
        `${API}/pipeline/refresh-quarter?year=${yearFilter}&quarter=${quarterFilter}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Reset ${data.reset_count} tasks`);
        fetchTasks();
      }
    } catch (error) {
      toast.error("Failed to refresh quarter");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6" data-testid="quarter-explorer-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Quarter Explorer</h1>
          <p className="text-muted-foreground text-sm mt-1">
            View and manage fund-quarter extraction tasks
          </p>
        </div>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-6 gap-4">
        {["pending", "processing", "success", "failed", "skipped", "stale"].map((status) => (
          <button
            key={status}
            onClick={() => { setStatusFilter(status === statusFilter ? "" : status); setOffset(0); }}
            className={`p-3 rounded-sm border transition-colors ${
              statusFilter === status 
                ? "border-primary bg-primary/10" 
                : "border-border bg-card hover:bg-muted/50"
            }`}
          >
            <p className="text-xs text-muted-foreground capitalize">{status}</p>
            <p className="font-mono text-xl">{statusCounts[status] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Filter by CIK..."
            value={cikFilter}
            onChange={(e) => { setCikFilter(e.target.value); setOffset(0); }}
            className="pl-10 bg-background border-border rounded-sm"
            data-testid="cik-filter-input"
          />
        </div>
        
        <Select value={yearFilter || "all"} onValueChange={(v) => { setYearFilter(v === "all" ? "" : v); setOffset(0); }}>
          <SelectTrigger className="w-[120px] rounded-sm">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map((y) => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={quarterFilter || "all"} onValueChange={(v) => { setQuarterFilter(v === "all" ? "" : v); setOffset(0); }}>
          <SelectTrigger className="w-[120px] rounded-sm">
            <SelectValue placeholder="Quarter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Quarters</SelectItem>
            <SelectItem value="1">Q1</SelectItem>
            <SelectItem value="2">Q2</SelectItem>
            <SelectItem value="3">Q3</SelectItem>
            <SelectItem value="4">Q4</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" onClick={fetchTasks} className="rounded-sm">
          <RefreshCw className="w-4 h-4" />
        </Button>

        {yearFilter && quarterFilter && (
          <Button variant="outline" onClick={refreshQuarter} className="rounded-sm">
            <Calendar className="w-4 h-4 mr-2" />
            Refresh {yearFilter} Q{quarterFilter}
          </Button>
        )}
      </div>

      {/* Tasks Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>CIK</TableHead>
                  <TableHead>Quarter</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Holdings</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell className="font-mono text-sm">{task.cik}</TableCell>
                    <TableCell className="font-mono">{task.year} Q{task.quarter}</TableCell>
                    <TableCell>
                      <Badge className={`status-${task.status} rounded-sm`}>{task.status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono">{task.raw_holdings_count || 0}</TableCell>
                    <TableCell className="font-mono">{task.retries || 0}</TableCell>
                    <TableCell className="text-sm">{formatDate(task.started_at)}</TableCell>
                    <TableCell className="text-sm">{formatDate(task.completed_at)}</TableCell>
                    <TableCell className="text-xs text-red-400 max-w-[150px] truncate" title={task.last_error}>
                      {task.last_error || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => retryTask(task.id)}
                          title="Retry task"
                          data-testid={`retry-task-${task.id}`}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => skipTask(task.id)}
                          title="Skip task"
                          data-testid={`skip-task-${task.id}`}
                        >
                          <SkipForward className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                      No tasks found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="rounded-sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="rounded-sm"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuarterExplorerPage;
